import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaTicketAlt, FaHourglassHalf, FaVideo, FaTrophy, FaStar } from "react-icons/fa";

const BASE_URL = "https://api.dreamtoday.in";

/* Map index to icon */
const ICONS = [FaTicketAlt, FaHourglassHalf, FaVideo, FaTrophy, FaStar];
const getIcon = (index) => ICONS[index % ICONS.length];

const HowItWorks = () => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BASE_URL}/api/admin/getallsteps`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.steps?.length > 0) {
          // Use the first steps document and sort by position if available, otherwise use array order
          const stepsArray = d.steps[0].steps;
          let sorted = [...stepsArray];
          
          // Try to sort by position if it exists
          if (stepsArray.length > 0 && stepsArray[0].position !== undefined) {
            sorted.sort((a, b) => (a.position || 0) - (b.position || 0));
          }
          
          setSteps(sorted);
        } else {
          setSteps([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching steps:", err);
        setSteps([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Fall back to skeleton count of 4 while loading
  const displaySteps = loading ? Array(4).fill(null) : steps;

  if (!loading && steps.length === 0) return null;

  return (
    <section className="relative py-24 bg-gradient-to-b from-white via-[#FFF7DB] to-white overflow-hidden">

      {/* ✨ SOFT GOLD WAVE BACKGROUND */}
      <div className="absolute inset-0 opacity-40">
        <svg viewBox="0 0 1200 600" className="w-full h-full">
          <defs>
            <linearGradient id="goldGradLight" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FDE68A" />
              <stop offset="50%" stopColor="#FACC15" />
              <stop offset="100%" stopColor="#CA8A04" />
            </linearGradient>
          </defs>
          <motion.path
            d="M0,300 C200,100 400,500 600,300 S1000,100 1200,300"
            stroke="url(#goldGradLight)"
            strokeWidth="2"
            fill="transparent"
            strokeDasharray="18 18"
            animate={{ strokeDashoffset: [0, -200] }}
            transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
          />
          <motion.path
            d="M0,350 C200,150 400,550 600,350 S1000,150 1200,350"
            stroke="url(#goldGradLight)"
            strokeWidth="1"
            fill="transparent"
            opacity="0.5"
            strokeDasharray="10 15"
            animate={{ strokeDashoffset: [0, -150] }}
            transition={{ repeat: Infinity, duration: 10, ease: "linear" }}
          />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">

        {/* TITLE */}
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl font-bold text-center mb-20"
        >
          <span className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 bg-clip-text text-transparent">
            How It Works
          </span>
        </motion.h2>

        {/* STEPS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {displaySteps.map((step, index) => {
            if (loading || !step) {
              // Skeleton
              return (
                <div key={index} className="relative animate-pulse">
                  <div className="bg-white/80 backdrop-blur-xl p-8 rounded-2xl text-center
                    border border-[#F5D77A] shadow-lg">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-100" />
                    <div className="h-5 bg-yellow-100 rounded mb-2 mx-auto w-2/3" />
                    <div className="h-4 bg-yellow-50 rounded mx-auto w-3/4" />
                  </div>
                  <div className="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-yellow-200" />
                </div>
              );
            }

            // Use index as fallback for position
            const displayPosition = step.position !== undefined ? step.position : index + 1;
            const Icon = getIcon(index); // Use index for icon selection

            return (
              <motion.div
                key={step._id || index}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                viewport={{ once: true }}
                className="relative group"
              >
                <div className="bg-white/80 backdrop-blur-xl p-8 rounded-2xl text-center
                  border border-[#F5D77A] shadow-lg hover:shadow-xl transition">

                  {/* ICON */}
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full
                    bg-gradient-to-r from-[#FFE9A8] to-[#FFD86B]
                    flex items-center justify-center
                    shadow-md group-hover:scale-110 transition">
                    <Icon className="text-3xl text-yellow-800" />
                  </div>

                  {/* Use 'text' or 'name' field based on what's available */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {step.text || step.name || `Step ${displayPosition}`}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {step.description || "Complete this step to proceed"}
                  </p>
                </div>

                {/* STEP NUMBER */}
                <div className="absolute -top-4 -right-4 w-10 h-10 rounded-full
                  bg-gradient-to-r from-yellow-500 to-yellow-600
                  flex items-center justify-center font-bold text-white shadow-md">
                  {displayPosition}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;